// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import type {CopilotChatReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import InputLabel from '@github-ui/input-label'
import {useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {RepositoryPicker as ItemPickerRepositoryPicker} from '@github-ui/item-picker/RepositoryPicker'
import {useCallback, useEffect, useState} from 'react'

import type {TimelineEventTextReference} from '../../timeline-events/timeline-events-types'
import {type DraftIssue, makeReferenceFromVersionedItem} from '../content-preview-types'
import {useContentPreview} from '../ContentPreviewContext'
import styles from './RepositoryPicker.module.css'
import {useDraftIssueRepository} from './use-draft-issue-repository'

type RepositoryPickerProps = {
  draftIssue: DraftIssue
  resolvedDraftIssueRepo: RepositoryPickerRepository | null | undefined
  loadingDraftIssueRepo: boolean
  topRepos: RepositoryPickerRepository[]
}

export function RepositoryPicker({
  draftIssue,
  resolvedDraftIssueRepo,
  loadingDraftIssueRepo,
  topRepos,
}: RepositoryPickerProps) {
  const [key, setKey] = useState<string | undefined>(undefined)
  const {repository: formRepository} = useIssueCreateMetadata()

  const onRepoSelected = useRepoSelectedCallback(draftIssue)

  const onSelect = useCallback(
    (newSelectedRepo: RepositoryPickerRepository | undefined) => {
      // Disallow deselection
      if (newSelectedRepo != null) {
        void onRepoSelected(newSelectedRepo.name, newSelectedRepo.owner.login)
      }
    },
    [onRepoSelected],
  )

  useDraftIssueRepository(draftIssue, resolvedDraftIssueRepo, loadingDraftIssueRepo, topRepos)

  // Side effect to ensure ItemPickerRepositoryPicker to memorize the selected non-top-repositories repo.
  useEffect(() => {
    if (
      !loadingDraftIssueRepo &&
      formRepository != null &&
      topRepos != null &&
      !topRepos.find(node => node.id === formRepository.id)
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setKey(formRepository.id)
    }
  }, [formRepository, loadingDraftIssueRepo, topRepos])

  // A UX only step to force a default repository from the top repositories list instead of letting
  // the ItemPickerRepositoryPicker to do so since it only does this once after the first render.
  const selectedRepo = formRepository ?? topRepos?.[0]
  // Prevents user change when:
  // 1. Waiting for streaming
  // 2. Waiting for resolving draft issue repo
  const readonly = draftIssue.isStreaming || loadingDraftIssueRepo

  return (
    <div className={styles.container}>
      {/* Not using htmlFor because the picker already has its own aria-label ("selected repo: [repo name]") */}
      <InputLabel className={styles.label} required>
        Repository
      </InputLabel>
      <ItemPickerRepositoryPicker
        key={key}
        initialRepository={selectedRepo}
        onSelect={onSelect}
        options={{hasIssuesEnabled: true, readonly}}
        topRepositories={topRepos}
        enforceAtleastOneSelected
      />
    </div>
  )
}

function useRepoSelectedCallback(draftIssue: DraftIssue) {
  const manager = useChatManager()
  const state = useChatState()
  const {updateItem} = useContentPreview()

  return useCallback(
    async (repoName: string, repoOwner: string) => {
      const repoNwo = `${repoOwner}/${repoName}`
      const instructions: TimelineEventTextReference = {
        type: 'text',
        name: `timeline-event: {"type": "switch-issue-repo", "markdownContent": "Changing repository to ${repoNwo}…"}`,
        text: `Update draft issue with tag \`${draftIssue.tag}\` to use the \`${repoNwo}\` repository. DO NOT ask the user for a template. If there is a template in the new repository that makes sense to use, use it. If you cannot find a template, draft a blank issue. Regenerate the title and description. Maintain all conversation context. Update the metadata to follow the new repository and template. DO NOT change the \`tag\` or \`parentTag\` fields.`,
      }

      const references: CopilotChatReference[] = [instructions]
      if (draftIssue.isUserEdited) {
        // Since references of a supported type get "deserialized" back as a versioned item in content preview,
        // we make sure this version contains any user updates that were made.
        // We don't update the template because the draft issue's content is still using the old/blank template
        const updatedDraftIssue = {
          ...draftIssue,
          ...(repoNwo && {repository: repoNwo}),
          // Mark content preview items that correspond to the references as not user-edited so that they don't automatically
          // get closed when the chat reducer clears the currentReferences.
          isUserEdited: false,
        }
        updateItem(updatedDraftIssue)
        references.push(makeReferenceFromVersionedItem(updatedDraftIssue))
      }
      await manager.sendChatMessage({
        thread: getSelectedThread(state),
        content: `Use the ${repoNwo} repository`,
        references: [...references, ...state.currentReferences],
        topic: state.currentTopic,
        context: state.context,
        customInstructions: state.customInstructions,
        model: state.model,
      })
    },
    [draftIssue, manager, state, updateItem],
  )
}
